// #595 Verträge im neuen Design: alle Mietverhältnisse gebündelt, filterbar nach Status, Art und Objekt.
// Zeile öffnet den Mieter (dort liegen Vertrag, Miete und „Neuer Vertrag“). Alte Links /admin/vertraege/:id
// führen zum Mieter des angelegten Vertrags.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, CalendarX, CaretDown, Check, Coins, Export, FileCsv, FileXls, Plus, Signature, Timer, WarningCircle } from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { alleSeiten } from '../../../lib/supabase/alleSeiten';
import { Chip, ImmoButton, KpiReihe, KpiTile, ListenSuche, MenueEintrag, ObjektFilter, Popover, SegmentedControl, Seitennavigation } from '../../../components/immobilien/ui';
import { eur, heuteIso } from '../../../lib/immobilien/format';
import { deDatum } from '../../../lib/immobilien/objektDetail';
import { seiteAus } from '../../../lib/immobilien/seiten';
import { objektOptionen, type MieterListenRoh } from '../../../lib/mieter/liste';
import type { ObjektKopfRoh } from '../../../lib/immobilien/objektKopf';
import {
  ART_LABEL, ART_REIHENFOLGE, ENDET_BALD_TAGE, FILTER_LABEL, FILTER_REIHENFOLGE, STATUS_ANZEIGE, baueVertragZeilen, basisVertraege, istArtFilter,
  istVertragFilter, kennzahlen, passtFilter, sortiere, vertragsnummernJeMieter, zaehle, type ArtFilter, type VertragFilter, type VertragZeile,
} from '../../../lib/vertraege/uebersicht';
import { exportCsv } from '../../../lib/export/exportCsv';
import { exportXlsx } from '../../../lib/export/exportXlsx';
import type { ExportColumn } from '../../../lib/export/types';

const PRO_SEITE = 50;
const KOPF_STIL: React.CSSProperties = { boxShadow: 'inset 0 0 0 1px var(--line)', fontWeight: 600 };
const SPALTEN = 'minmax(150px,1.3fr) minmax(150px,1.2fr) 112px 92px 92px 92px 100px 104px';

interface Daten {
  mieter: (MieterListenRoh & { vertragsart?: string | null })[];
  objekte: ObjektKopfRoh[];
  einheiten: { id: string; bezeichnung: string | null; objekt_id: string | null }[];
  vertragsnummern: Map<string, string>;
}

async function lade(): Promise<Daten> {
  const [mieter, objekte, einheiten, vertraege] = await Promise.all([
    alleSeiten<Daten['mieter'][number]>((von, bis) => supabase.from('mieter')
      .select('id, objekt_id, einheit_id, einheit, anrede, vorname, nachname, firma_name, status, kaltmiete, nebenkosten_vorauszahlung, heizkosten, weitere_kosten, miet_historie, mietbeginn, mietende, vertragsart, email')
      .order('id').range(von, bis)),
    alleSeiten<ObjektKopfRoh>((von, bis) => supabase.from('objekte').select('*').eq('ist_verwaltung', false).order('objektnummer').range(von, bis)),
    alleSeiten<Daten['einheiten'][number]>((von, bis) => supabase.from('objekt_einheiten').select('id, bezeichnung, objekt_id').order('id').range(von, bis)),
    alleSeiten<{ mieter_id: string | null; vertragsnummer: string | null; created_at: string | null }>((von, bis) => supabase.from('vertraege')
      .select('mieter_id, vertragsnummer, created_at').order('id').range(von, bis)),
  ]);
  return { mieter, objekte, einheiten, vertragsnummern: vertragsnummernJeMieter(vertraege) };
}

const EXPORT_SPALTEN: ExportColumn[] = [
  { key: 'name', label: 'Mieter' },
  { key: 'objektName', label: 'Objekt' },
  { key: 'einheit', label: 'Einheit' },
  { key: 'artText', label: 'Vertragsart' },
  { key: 'beginn', label: 'Beginn', format: v => deDatum(v as string | null) },
  { key: 'ende', label: 'Ende', format: v => (v ? deDatum(v as string) : 'unbefristet') },
  { key: 'kalt', label: 'Kaltmiete', format: v => eur(Number(v)) },
  { key: 'gesamt', label: 'Gesamtmiete', format: v => eur(Number(v)) },
  { key: 'status', label: 'Status', format: v => STATUS_ANZEIGE[v as VertragZeile['status']].label },
  { key: 'vertragsnummer', label: 'Vertragsnummer' },
];

export default function VertraegeSeite() {
  const navigate = useNavigate();
  const { id: altId } = useParams<{ id?: string }>();
  const [params, setParams] = useSearchParams();
  const heute = heuteIso();
  const [daten, setDaten] = useState<Daten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const [suche, setSuche] = useState('');

  useEffect(() => {
    let aktiv = true;
    setFehler(null);
    lade().then(d => { if (aktiv) setDaten(d); }, (e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler'); });
    return () => { aktiv = false; };
  }, [runde]);

  // Alte Links (Suche, Verlauf): /admin/vertraege/:id → Mieter des angelegten Vertrags
  useEffect(() => {
    if (!altId) return;
    let aktiv = true;
    void supabase.from('vertraege').select('mieter_id').eq('id', altId).maybeSingle().then(({ data }) => {
      if (!aktiv) return;
      const mieterId = (data as { mieter_id: string | null } | null)?.mieter_id;
      navigate(mieterId ? `/admin/mieter/${encodeURIComponent(mieterId)}` : '/admin/vertraege', { replace: true });
    });
    return () => { aktiv = false; };
  }, [altId, navigate]);

  const filter: VertragFilter = istVertragFilter(params.get('f')) ? params.get('f') as VertragFilter : 'laufend';
  const art: ArtFilter = istArtFilter(params.get('art')) ? params.get('art') as ArtFilter : 'alle';
  const objekt = params.get('obj') ?? 'alle';

  const setzeParam = useCallback((schluessel: string, wert: string, standard: string) => {
    setParams(p => {
      const n = new URLSearchParams(p);
      if (wert === standard) n.delete(schluessel); else n.set(schluessel, wert);
      if (schluessel !== 'seite') n.delete('seite');
      return n;
    });
  }, [setParams]);
  const sucheAendern = (v: string) => {
    setSuche(v);
    if (params.get('seite')) setParams(p => { const n = new URLSearchParams(p); n.delete('seite'); return n; }, { replace: true });
  };

  const zeilen = useMemo(() => (daten ? baueVertragZeilen({ ...daten, heute }) : []), [daten, heute]);
  const k = useMemo(() => kennzahlen(zeilen), [zeilen]);
  const optionen = useMemo(() => (daten ? objektOptionen(daten.objekte, zeilen.map(z => ({ objektId: z.objektId, inaktiv: z.status === 'beendet' }))) : []), [daten, zeilen]);
  const basis = useMemo(() => basisVertraege(zeilen, objekt, suche, art), [zeilen, objekt, suche, art]);
  const zaehler = useMemo(() => zaehle(basis), [basis]);
  const sichtbar = useMemo(() => sortiere(basis.filter(z => passtFilter(z, filter)), filter), [basis, filter]);

  const listeOben = useRef<HTMLDivElement>(null);
  const seite = useMemo(() => seiteAus(sichtbar, Number(params.get('seite') ?? '1'), PRO_SEITE), [sichtbar, params]);
  const zuSeite = (n: number) => { setzeParam('seite', String(n), '1'); listeOben.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const oeffne = (id: string) => navigate(`/admin/mieter/${encodeURIComponent(id)}`);
  const exportiere = (format: 'csv' | 'xlsx') => {
    const config = {
      title: 'Verträge',
      columns: EXPORT_SPALTEN,
      data: sichtbar as unknown as Record<string, unknown>[],
      stats: { items: [{ label: FILTER_LABEL[filter], value: sichtbar.length }, { label: 'Art', value: ART_LABEL[art] }] },
    };
    if (format === 'csv') exportCsv(config); else exportXlsx(config);
  };

  const datum = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{datum}</span>
          <h1 className="m-0" style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05 }}>Verträge</h1>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>
            {daten ? `${k.laufend} laufende Mietverhältnisse · ${k.endetBald} enden in den nächsten ${ENDET_BALD_TAGE} Tagen` : 'Wird geladen …'}
          </span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <Popover label="Export" breite={200}
            ausloeser={({ offen, umschalten, ref }) => (
              <ImmoButton ref={ref} groesse={40} icon={Export} aria-haspopup="menu" aria-expanded={offen} onClick={umschalten} disabled={!daten || sichtbar.length === 0} style={KOPF_STIL}
                data-tip="Die gefilterte Liste exportieren">Export</ImmoButton>
            )}>
            {schliessen => (
              <>
                <MenueEintrag onClick={() => { schliessen(); exportiere('csv'); }}><FileCsv size={16} color="var(--ok)" />CSV</MenueEintrag>
                <MenueEintrag onClick={() => { schliessen(); exportiere('xlsx'); }}><FileXls size={16} color="var(--info)" />Excel (XLSX)</MenueEintrag>
              </>
            )}
          </Popover>
          <ImmoButton art="akzent" groesse={40} icon={Plus} onClick={() => navigate('/admin/mieter?neu=1')} style={{ padding: '0 16px' }}
            data-tip="Neues Mietverhältnis – legt einen neuen Mieter mit Vertragsdaten an">Neues Mietverhältnis</ImmoButton>
        </div>
      </header>

      {fehler && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Verträge konnten nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{fehler}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={() => setRunde(r => r + 1)}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {daten && (
        <KpiReihe minBreite={190}>
          <KpiTile layout="kachel" label="Laufende Verträge" wert={k.laufend.toLocaleString('de-DE')} icon={Signature} ton="ok" onClick={() => setzeParam('f', 'laufend', 'laufend')} />
          <KpiTile layout="kachel" label="Sollmiete / Monat" wert={eur(k.sollmiete, { nachkomma: 0 })} icon={Coins} ton="info" />
          <KpiTile layout="kachel" label={`Enden in ${ENDET_BALD_TAGE} Tagen`} wert={k.endetBald} icon={CalendarX} ton={k.endetBald ? 'warn' : 'neutral'} onClick={() => setzeParam('f', 'endet', 'laufend')} />
          <KpiTile layout="kachel" label="Befristet" wert={k.befristet} icon={Timer} ton="neutral" onClick={() => setzeParam('art', 'befristet', 'alle')} />
        </KpiReihe>
      )}

      <div ref={listeOben} className="flex flex-wrap items-center" style={{ gap: 10, scrollMarginTop: 16 }}>
        <ListenSuche wert={suche} onChange={sucheAendern} platzhalter="Mieter, Objekt, Einheit, Vertragsnr. …" ariaLabel="Verträge durchsuchen" />
        <SegmentedControl art="akzent" ariaLabel="Status" wert={filter} onChange={f => setzeParam('f', f, 'laufend')}
          segmente={FILTER_REIHENFOLGE.map(f => ({ key: f, label: `${FILTER_LABEL[f]} · ${zaehler[f]}` }))} />
        <Popover label="Vertragsart" breite={210}
          ausloeser={({ offen, umschalten, ref }) => (
            <ImmoButton ref={ref} groesse={40} aria-haspopup="menu" aria-expanded={offen} onClick={umschalten} style={KOPF_STIL}>
              {ART_LABEL[art]} <CaretDown size={12} weight="bold" />
            </ImmoButton>
          )}>
          {schliessen => ART_REIHENFOLGE.map(a => (
            <MenueEintrag key={a} onClick={() => { schliessen(); setzeParam('art', a, 'alle'); }}>
              <span style={{ width: 16 }}>{a === art && <Check size={14} weight="bold" />}</span>{ART_LABEL[a]}
            </MenueEintrag>
          ))}
        </Popover>
        <ObjektFilter optionen={optionen} wert={objekt} onChange={o => setzeParam('obj', o, 'alle')} alleAnzahl={k.laufend} />
      </div>

      {!daten && !fehler && (
        <section aria-busy="true" aria-label="Verträge werden geladen" className="flex flex-col" style={{ gap: 8 }}>
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 56, borderRadius: 16, background: 'var(--fill2)' }} />)}
        </section>
      )}

      {daten && sichtbar.length === 0 && (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: 34, gap: 10, fontSize: 14, color: 'var(--ink3)' }}>
          Keine Verträge unter diesen Kriterien gefunden
          {(suche || objekt !== 'alle' || filter !== 'laufend' || art !== 'alle') && (
            <ImmoButton onClick={() => { setSuche(''); setParams(new URLSearchParams()); }}>Filter zurücksetzen</ImmoButton>
          )}
        </div>
      )}

      {daten && sichtbar.length > 0 && (
        <section className="immo-glas overflow-x-auto" style={{ borderRadius: 26, padding: 6 }} aria-label="Verträge">
          <div role="table" className="flex flex-col" style={{ minWidth: 940 }}>
            <div role="row" className="grid" style={{ gridTemplateColumns: SPALTEN, gap: 10, padding: '12px 14px', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
              <span role="columnheader">Mieter</span><span role="columnheader">Objekt · Einheit</span><span role="columnheader">Art</span>
              <span role="columnheader">Beginn</span><span role="columnheader">Ende</span>
              <span role="columnheader" style={{ textAlign: 'right' }}>Kalt</span><span role="columnheader" style={{ textAlign: 'right' }}>Gesamt</span>
              <span role="columnheader" style={{ textAlign: 'right' }}>Status</span>
            </div>
            {seite.eintraege.map(z => {
              const st = STATUS_ANZEIGE[z.status];
              return (
                <div key={z.id} role="row" tabIndex={0} aria-label={`${z.name} öffnen`} onClick={() => oeffne(z.id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); oeffne(z.id); } }}
                  className="immo-hover-flaeche grid items-center cursor-pointer" style={{ gridTemplateColumns: SPALTEN, gap: 10, padding: '10px 14px', borderRadius: 16, fontSize: 13 }}>
                  <span role="cell" className="flex flex-col min-w-0">
                    <b className="truncate" style={{ fontWeight: 800 }}>{z.name}</b>
                    {z.vertragsnummer && <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{z.vertragsnummer}</span>}
                  </span>
                  <span role="cell" className="flex flex-col min-w-0">
                    <span className="truncate" style={{ fontWeight: 600 }}>{z.objektName || '–'}</span>
                    <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{z.einheit || 'ohne Einheit'}</span>
                  </span>
                  <span role="cell" className="truncate" style={{ color: 'var(--ink2)' }} data-tip={z.artText || undefined}>{z.art === 'ohne' ? '–' : ART_LABEL[z.art]}</span>
                  <span role="cell" className="immo-zahl">{z.beginn ? deDatum(z.beginn) : '–'}</span>
                  <span role="cell" className="immo-zahl" style={{ color: z.endetBald ? 'var(--warn)' : z.ende ? 'var(--ink)' : 'var(--ink3)', fontWeight: z.endetBald ? 800 : undefined }}>
                    {z.ende ? deDatum(z.ende) : 'unbefristet'}
                  </span>
                  <span role="cell" className="immo-zahl" style={{ textAlign: 'right' }}>{eur(z.kalt, { nachkomma: 0 })}</span>
                  <b role="cell" className="immo-zahl" style={{ textAlign: 'right', fontWeight: 800 }}>{eur(z.gesamt)}</b>
                  <span role="cell" className="flex justify-end"><Chip ton={st.ton} groesse="xs">{st.label}</Chip></span>
                </div>
              );
            })}
          </div>
        </section>
      )}
      {daten && sichtbar.length > 0 && (
        <Seitennavigation seite={seite.seite} seiten={seite.seiten} von={seite.von} bis={seite.bis} gesamt={seite.gesamt} einheit="Verträge" onSeite={zuSeite} />
      )}
    </>
  );
}
